import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { platformApi } from '@/features/platform/api';
import { friendlyMessage, isApiError } from '@/lib/api/api-error';
import { refreshSession } from '@/lib/api/client';
import { sessionStore } from '@/lib/auth/session-store';
import { resetBranding } from '@/lib/branding';
import { meQuery, navigationQuery } from './queries';

/**
 * Switching between the platform console and a tenant (support) session. While a switch is in
 * flight the shell shows a full-screen transition so stale screens never flash with the new token.
 */
let switching: string | null = null;
let lastSupportTenantId: string | null = null;
const listeners = new Set<() => void>();

function setSwitching(next: string | null): void {
  switching = next;
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useSupportSwitching(): string | null {
  return useSyncExternalStore(
    subscribe,
    () => switching,
    () => switching,
  );
}

export function isSwitchingSession(): boolean {
  return switching !== null;
}

/** Tenant to return to when a support session ends (survives the token swap, not a reload). */
export function supportReturnTenantId(): string | null {
  return sessionStore.getSupport()?.tenantId ?? lastSupportTenantId;
}

export interface SupportSessionControls {
  start: (tenantId: string, tenantName: string) => Promise<boolean>;
  end: (options?: { reason?: string; alreadyRestored?: boolean }) => Promise<void>;
}

export function useSupportSession(): SupportSessionControls {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const reloadSession = useCallback(async () => {
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' });
    await Promise.all([
      queryClient.fetchQuery({ ...meQuery, staleTime: 0 }),
      queryClient.fetchQuery({ ...navigationQuery, staleTime: 0 }),
    ]);
  }, [queryClient]);

  const start = useCallback(
    async (tenantId: string, tenantName: string) => {
      setSwitching(`Opening ${tenantName} in read-only support mode…`);
      try {
        const res = await platformApi.startSupportSession(tenantId);
        lastSupportTenantId = tenantId;
        sessionStore.beginSupport(res.accessToken, res.expiresIn, tenantId);
        await reloadSession();
        await navigate({ to: '/' });
        toast.info(`You’re viewing ${tenantName} as support`, {
          description: 'Read-only. Every page you open is recorded in their audit log.',
        });
        return true;
      } catch (error) {
        if (sessionStore.getSupport()) {
          // the token was swapped but loading the tenant failed: go back to the platform
          await refreshSession();
          await reloadSession().catch(() => undefined);
        }
        // FORBIDDEN carries the reason (e.g. the tenant hasn't granted access): show it verbatim
        const description =
          isApiError(error) && error.code === 'FORBIDDEN' && error.message
            ? error.message
            : friendlyMessage(error);
        toast.error('Couldn’t start a support session', { description, duration: 8000 });
        return false;
      } finally {
        setSwitching(null);
      }
    },
    [navigate, reloadSession],
  );

  const end = useCallback(
    async ({ reason, alreadyRestored = false }: { reason?: string; alreadyRestored?: boolean } = {}) => {
      if (switching) return;
      const tenantId = supportReturnTenantId();
      setSwitching('Returning to the platform console…');
      try {
        // the platform session's refresh cookie is still valid: exchange it for a platform token
        const ok = alreadyRestored || (await refreshSession());
        if (!ok) {
          queryClient.clear();
          await navigate({ to: '/login' });
          return;
        }
        resetBranding();
        await reloadSession();
        lastSupportTenantId = null;
        if (tenantId) await navigate({ to: '/platform/tenants/$tenantId', params: { tenantId } });
        else await navigate({ to: '/platform/tenants' });
        if (reason) toast.info(reason);
      } finally {
        setSwitching(null);
      }
    },
    [navigate, queryClient, reloadSession],
  );

  return { start, end };
}
